import { useState } from "react";
import { Dialog, DialogPanel, DialogTitle } from "@headlessui/react";
import { useForm, type SubmitHandler } from "react-hook-form";
import { render, pretty } from "@react-email/render";

import ComplaintsTemplate from "./ComplaintsTemplate";

type Inputs = {
  description: string;
  date: Date;
  ndis_name: string;
  persons: string;
  name: string;
  relationship: string;
  contact: string;
  contact_method: string;
};

const ComplaintsForm = () => {
  const [loading, setLoading] = useState(false);
  const [isOpen, setIsOpen] = useState(false);
  const [submitTitle, setSubmitTitle] = useState<string>();
  const [submitMessage, setSubmitMessage] = useState<string>();

  const {
    register,
    formState: { errors },
    handleSubmit,
  } = useForm<Inputs>();

  const closeModal = () => {
    setIsOpen(false);
  };

  const openModal = () => {
    setIsOpen(true);
    setLoading(false);
  };

  const onSubmit: SubmitHandler<Inputs> = async (data) => {
    setLoading(true);

    const emailHtml = await pretty(
      await render(
        <ComplaintsTemplate
          description={data.description}
          date={data.date}
          ndis_name={data.ndis_name}
          persons={data.persons}
          name={data.name}
          relationship={data.relationship}
          contact={data.contact}
          contact_method={data.contact_method}
        />,
      ),
    );

    await fetch("https://next-api.useplunk.com/v1/send", {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
        Authorization: import.meta.env.VITE_EMAIL_API,
      },
      body: JSON.stringify({
        to: import.meta.env.VITE_EMAIL_ADDRESS,
        subject: "New Complaint Submission",
        body: emailHtml,
        from: {
          name: "Provido Complaints Form",
          email: "donotreply@provido.com.au",
        },
      }),
    })
      .then(() => {
        setSubmitTitle("Thank You");
        setSubmitMessage(
          "Thank you for sending your complaint to Provido. If you have requested we contact you regarding your complaint, you should hear from us soon.",
        );
        openModal();
      })
      .catch((error) => {
        console.log(error);
        setSubmitTitle("Something Went Wrong");
        setSubmitMessage(
          "Looks like something went wrong. Please try again or email us directly using the email addresses on this page. Sorry for any inconvenience.",
        );
        openModal();
      });
  };

  return (
    <>
      <form
        onSubmit={handleSubmit(onSubmit)}
        className="mx-auto mt-8 flex max-w-xl flex-col gap-4 px-4"
      >
        <div>
          <label htmlFor="description">
            Describe the complaint or incident:{" "}
            <span className="text-red-500">
              * {errors.description && <span>Description is required.</span>}
            </span>
          </label>
          <textarea
            {...register("description", { required: true })}
            className="form-field"
          />
        </div>
        <div>
          <label htmlFor="date">Date of incident (if applicable):</label>
          <input
            type="date"
            {...register("date", { required: false })}
            className="form-field"
          />
        </div>
        <div>
          <label htmlFor="ndis_name">
            Names or NDIS number/s of participant/s involved:
          </label>
          <input
            type="text"
            {...register("ndis_name", { required: false })}
            className="form-field"
          ></input>
        </div>
        <div>
          <label htmlFor="persons">Who else was involved?:</label>
          <input
            type="text"
            {...register("persons", { required: false })}
            className="form-field"
          ></input>
        </div>
        <hr className="mt-6 mb-5 border-t border-blue/30" />
        <div>
          <label htmlFor="name">Your name (can be anonymous):</label>
          <input
            type="text"
            {...register("name", { required: false })}
            className="form-field"
          />
        </div>
        <div>
          <label htmlFor="relationship">Your relationship to Provido:</label>
          <select
            {...register("relationship", { required: false })}
            className="form-field"
          >
            <option hidden selected>
              Please choose an option
            </option>
            <option value="Person we Support">Person We Support</option>
            <option value="Family member / Carer">Family Member / Carer</option>
            <option value="Staff Member">Staff Member</option>
            <option value="External Service Provider">
              External Service Provider
            </option>
            <option value="Member of Community">Member of Community</option>
            <option value="Other / Prefer Not to Say">
              Other / Prefer Not to Say
            </option>
          </select>
        </div>
        <div>
          <label htmlFor="contact">
            Do you wish to be contacted regarding this complaint?:
          </label>
          <div className="pt-2">
            <input
              type="radio"
              {...register("contact", { required: false })}
              value="Yes"
              name="contact"
              className="mx-2 accent-blue"
            />{" "}
            <span className="mr-8 font-body">Yes</span>
            <input
              type="radio"
              {...register("contact", { required: false })}
              value="No"
              name="contact"
              className="mr-2 accent-blue"
            />{" "}
            <span className="font-body">No</span>
          </div>
        </div>
        <div>
          <label htmlFor="contact_method">
            If Yes, please provide an email address or phone number:
          </label>
          <input
            type="text"
            {...register("contact_method", { required: false })}
            className="form-field"
          />
        </div>
        <div className="flex flex-row justify-between">
          <button
            type="submit"
            disabled={loading}
            className="w-fit rounded-xl bg-blue px-4 py-2 font-semibold text-white transition hover:cursor-pointer hover:bg-purple disabled:bg-blue/10 hover:disabled:bg-blue/10"
          >
            Submit
          </button>
          <button
            type="reset"
            className="hover:bg-grey w-fit rounded-xl bg-gray-200 px-4 py-2 font-semibold hover:cursor-pointer hover:bg-gray-400 hover:text-white"
          >
            Reset
          </button>
        </div>
      </form>

      {/* Submission Dialog Box */}
      <Dialog
        open={isOpen}
        onClose={() => setIsOpen(false)}
        className="relative z-50"
      >
        <div className="fixed top-0 left-0 z-49 h-screen w-screen bg-lightblue/20"></div>
        <div className="fixed inset-0 z-50 flex w-screen items-center justify-center">
          <DialogPanel className="max-w-lg space-y-4 rounded-xl border-2 border-lightblue bg-white p-6">
            <DialogTitle className="pb-2 font-display text-2xl text-blue">
              {submitTitle}
            </DialogTitle>
            <p className="text-grey font-body">{submitMessage}</p>
            <div className="flex justify-end">
              <button
                className="w-fit rounded-xl bg-blue px-4 py-2 font-semibold text-white transition hover:cursor-pointer hover:bg-darkblue"
                onClick={() => closeModal()}
              >
                Close
              </button>
            </div>
          </DialogPanel>
        </div>
      </Dialog>
    </>
  );
};

export default ComplaintsForm;
